import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { instructorApi } from "../../../services/instructorApi";
import OnboardingStepFrame from "../../../components/instructor/OnboardingStepFrame";

const STYLES = [
  "Hatha",
  "Vinyasa",
  "Ashtanga",
  "Yin",
  "Restorative",
  "Prenatal",
  "Postnatal",
  "Meditation",
  "Mindfulness Meditation",
  "Pranayama",
  "Kundalini",
  "Power Yoga",
  "Iyengar",
  "Hot Yoga",
  "Bikram",
  "Aerial Yoga",
  "Acro Yoga",
  "Chair Yoga",
  "Senior Yoga",
  "Kids Yoga",
  "Yoga Therapy",
  "Therapeutic Yoga",
  "Yoga Nidra",
  "Sound Healing",
  "Ayurveda",
  "Wellness",
  "Private Yoga Sessions",
  "Group Yoga Classes",
  "Live Online Class",
  "Workshop",
];
const FORMATS = ["Live online", "1:1", "Group"];
const LEVELS = ["Beginner", "Intermediate", "Advanced", "All Levels"];

const inputClass =
  "mt-1 w-full border border-gray-300 px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-gray-500";

const TONES = {
  emerald: {
    active: "bg-emerald-600 border-emerald-600 text-white shadow-sm",
    idle: "bg-white border-emerald-200 text-emerald-800 hover:bg-emerald-50",
    panel: "border-emerald-100 bg-emerald-50/40",
    title: "text-emerald-900",
  },
  teal: {
    active: "bg-teal-600 border-teal-600 text-white shadow-sm",
    idle: "bg-white border-teal-200 text-teal-800 hover:bg-teal-50",
    panel: "border-teal-100 bg-teal-50/40",
    title: "text-teal-900",
  },
  amber: {
    active: "bg-amber-500 border-amber-500 text-white shadow-sm",
    idle: "bg-white border-amber-200 text-amber-800 hover:bg-amber-50",
    panel: "border-amber-100 bg-amber-50/40",
    title: "text-amber-900",
  },
};

const LEVEL_TONES = {
  Beginner: {
    active: "bg-teal-600 border-teal-600 text-white shadow-sm",
    idle: "bg-white border-teal-200 text-teal-800 hover:bg-teal-50",
  },
  Intermediate: {
    active: "bg-amber-500 border-amber-500 text-white shadow-sm",
    idle: "bg-white border-amber-200 text-amber-800 hover:bg-amber-50",
  },
  Advanced: {
    active: "bg-rose-600 border-rose-600 text-white shadow-sm",
    idle: "bg-white border-rose-200 text-rose-800 hover:bg-rose-50",
  },
  "All Levels": {
    active: "bg-violet-600 border-violet-600 text-white shadow-sm",
    idle: "bg-white border-violet-200 text-violet-800 hover:bg-violet-50",
  },
};

function ToggleGroup({ options, selected, onToggle, tone = "emerald", optionTones }) {
  const base = TONES[tone] || TONES.emerald;
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = selected.includes(opt);
        const optTone = optionTones?.[opt] || base;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            aria-pressed={active}
            className={`rounded-xl border px-3 py-1.5 text-sm font-medium transition ${
              active ? optTone.active : optTone.idle
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export default function ExpertiseStep() {
  const { instructor, refreshInstructor } = useAuth();
  const navigate = useNavigate();
  const [expertise, setExpertise] = useState([]);
  const [formats, setFormats] = useState([]);
  const [levels, setLevels] = useState([]);
  const [years, setYears] = useState("");
  const [custom, setCustom] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!instructor?.profile) return;
    setExpertise(instructor.profile.expertise || []);
    setFormats(instructor.profile.teaching_formats || []);
    setLevels(instructor.profile.levels_taught || []);
    setYears(
      instructor.profile.years_teaching != null
        ? String(instructor.profile.years_teaching)
        : ""
    );
  }, [instructor]);

  function toggle(list, setList, value) {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  }

  async function handleSave(e) {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      let tags = expertise;
      if (custom.trim()) {
        tags = [...new Set([...tags, custom.trim()])];
      }
      await instructorApi.saveStep("expertise", {
        expertise: tags,
        teaching_formats: formats,
        levels_taught: levels,
        years_teaching: years === "" ? null : Number(years),
      });
      await refreshInstructor();
      navigate("/instructor/onboarding/credentials");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <OnboardingStepFrame
      title="Your teaching expertise"
      subtitle="Select at least 2 styles or specialties, and years teaching."
    >
      {error && (
        <div className="mb-4 border border-red-300 bg-red-50 text-red-800 text-sm px-3 py-2 rounded-xl">
          {error}
        </div>
      )}
      <form onSubmit={handleSave} className="space-y-5 rounded-2xl border border-emerald-100 bg-white/90 p-5 shadow-sm sm:p-6">
        <label className="block">
          <span className="text-sm text-gray-800">Years teaching</span>
          <input
            type="number"
            min={0}
            max={50}
            required
            className={inputClass}
            value={years}
            onChange={(e) => setYears(e.target.value)}
          />
        </label>
        <div className={`rounded-xl border p-4 ${TONES.emerald.panel}`}>
          <p className={`text-sm font-semibold mb-2 ${TONES.emerald.title}`}>Styles & specialties</p>
          <ToggleGroup
            options={STYLES}
            selected={expertise}
            tone="emerald"
            onToggle={(v) => toggle(expertise, setExpertise, v)}
          />
          <input
            className={`${inputClass} mt-3 rounded-lg border-emerald-200`}
            placeholder="Add custom tag"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
          />
        </div>
        <div className={`rounded-xl border p-4 ${TONES.teal.panel}`}>
          <p className={`text-sm font-semibold mb-2 ${TONES.teal.title}`}>Teaching formats</p>
          <ToggleGroup
            options={FORMATS}
            selected={formats}
            tone="teal"
            onToggle={(v) => toggle(formats, setFormats, v)}
          />
        </div>
        <div className={`rounded-xl border p-4 ${TONES.amber.panel}`}>
          <p className={`text-sm font-semibold mb-2 ${TONES.amber.title}`}>Levels taught</p>
          <ToggleGroup
            options={LEVELS}
            selected={levels}
            tone="amber"
            optionTones={LEVEL_TONES}
            onToggle={(v) => toggle(levels, setLevels, v)}
          />
        </div>
        <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-200">
          <Link to="/instructor/onboarding/basics" className="text-sm text-gray-600 underline">
            Back
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="bg-emerald-700 text-white px-4 py-2 text-sm disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save & continue"}
          </button>
        </div>
      </form>
    </OnboardingStepFrame>
  );
}
